import type { LapRecord } from './replay';

/**
 * Local persistence, one entry per date. Everything is wrapped in try/catch:
 * storage can be missing (private mode, embedded frames) and the game must
 * still run.
 */
export interface MedalFlags {
  bronze: boolean;
  silver: boolean;
  gold: boolean;
  author: boolean;
}

export interface DayRecord {
  version: 1;
  date: string;
  trackHash: string;
  physicsHash: string;
  best: LapRecord | null;
  /** Best individual sector times from valid laps, ms. */
  bestSectors: (number | null)[];
  medals: MedalFlags;
  laps: number;
  validLaps: number;
  recent: { ms: number; valid: boolean; at: number }[];
}

const PREFIX = 'granpremio:v2:day:';

/** One entry per exact circuit and car, so a dev preview, era override or reroll never touches the daily record. */
function keyOf(date: string, trackHash: string, physicsHash: string): string {
  return `${PREFIX}${date}:${trackHash}:${physicsHash}`;
}

export function emptyDay(date: string, trackHash: string, physicsHash: string): DayRecord {
  return {
    version: 1,
    date,
    trackHash,
    physicsHash,
    best: null,
    bestSectors: [null, null, null],
    medals: { bronze: false, silver: false, gold: false, author: false },
    laps: 0,
    validLaps: 0,
    recent: [],
  };
}

export function loadDay(date: string, trackHash: string, physicsHash: string): DayRecord {
  try {
    const raw = globalThis.localStorage?.getItem(keyOf(date, trackHash, physicsHash));
    if (raw) {
      const d = JSON.parse(raw) as DayRecord;
      // Records only count for the exact circuit and physics they were driven on.
      if (d.version === 1 && d.trackHash === trackHash && d.physicsHash === physicsHash) return d;
    }
  } catch {
    /* storage unavailable or corrupt: start fresh */
  }
  return emptyDay(date, trackHash, physicsHash);
}

export function saveDay(day: DayRecord): boolean {
  try {
    globalThis.localStorage?.setItem(keyOf(day.date, day.trackHash, day.physicsHash), JSON.stringify(day));
    return true;
  } catch {
    return false;
  }
}

export function listStoredDates(): string[] {
  const out: string[] = [];
  try {
    const ls = globalThis.localStorage;
    if (!ls) return out;
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k?.startsWith(PREFIX)) out.push(k.slice(PREFIX.length).split(':')[0]);
    }
  } catch {
    /* ignore */
  }
  return [...new Set(out)].sort();
}

export function loadSetting<T>(key: string, fallback: T): T {
  try {
    const raw = globalThis.localStorage?.getItem('granpremio:setting:' + key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function saveSetting<T>(key: string, value: T): void {
  try {
    globalThis.localStorage?.setItem('granpremio:setting:' + key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}
